import { useEffect, useMemo, useState } from 'react';
import { viabilityLabel, type AppRole } from '@isbd/shared';
import { kuunda } from '@/lib/kuunda';
import { useAuth } from '@/features/auth/AuthProvider';
import { AdminEmpty, AdminKpiCard, AdminLoadingGrid, AdminPageHeader } from '@/features/admin/ui';
import { ListSkeleton } from '@/features/ui/skeleton';
import { useToast } from '@/features/ui/toast/useToast';
import '@/features/admin/admin.css';

export function AdminDashboardPage() {
  const toast = useToast();
  const [stats, setStats] = useState({
    users: 0,
    staff: 0,
    projects: 0,
    viable: 0,
    registrations: 0,
    openConcerns: 0,
    unreadNotifs: 0,
    inquiries: 0,
    visitors7d: 0,
    pageviews7d: 0,
    sessions7d: 0,
    conversions7d: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      const from7 = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const [u, staff, p, viable, r, concerns, notifs, inquiries, audience] = await Promise.all([
        kuunda.from('profiles').select('*', { count: 'exact', head: true }).eq('role', 'user'),
        kuunda.from('profiles').select('*', { count: 'exact', head: true }).in('role', ['admin', 'advisor', 'jury']),
        kuunda.from('projects').select('*', { count: 'exact', head: true }),
        kuunda.from('projects').select('*', { count: 'exact', head: true }).eq('viability', 'viable'),
        kuunda.from('contest_registrations').select('*', { count: 'exact', head: true }),
        kuunda.from('messages').select('*', { count: 'exact', head: true }).eq('is_preoccupation', true).eq('status', 'open'),
        kuunda.from('notifications').select('*', { count: 'exact', head: true }).eq('is_read', false),
        kuunda.from('marketplace_inquiries').select('*', { count: 'exact', head: true }).neq('status', 'closed'),
        kuunda.rpc('get_audience_stats', { p_from: from7, p_to: new Date().toISOString() }),
      ]);
      const firstErr =
        u.error?.message ||
        staff.error?.message ||
        p.error?.message ||
        viable.error?.message ||
        r.error?.message ||
        concerns.error?.message ||
        notifs.error?.message ||
        inquiries.error?.message;
      if (firstErr) toast.error(firstErr);
      // Audience optionnelle (migration pas encore appliquée)
      const ak = (audience.data as { kpis?: { visitors?: number; pageviews?: number; sessions?: number; conversions?: number } } | null)?.kpis;
      setStats({
        users: u.count ?? 0,
        staff: staff.count ?? 0,
        projects: p.count ?? 0,
        viable: viable.count ?? 0,
        registrations: r.count ?? 0,
        openConcerns: concerns.count ?? 0,
        unreadNotifs: notifs.count ?? 0,
        inquiries: inquiries.count ?? 0,
        visitors7d: ak?.visitors ?? 0,
        pageviews7d: ak?.pageviews ?? 0,
        sessions7d: ak?.sessions ?? 0,
        conversions7d: ak?.conversions ?? 0,
      });
      setLoading(false);
    })();
  }, []);

  return (
    <div className="admin-page">
      <AdminPageHeader
        title="Vue d’ensemble"
        description="Indicateurs clés de la plateforme — cliquez une carte pour ouvrir le module."
      />
      {loading ? (
        <AdminLoadingGrid count={12} />
      ) : (
        <>
          <div className="admin-kpi-grid">
            <AdminKpiCard
              to="/admin/audience"
              label="Visiteurs (7 j)"
              value={stats.visitors7d}
              icon="chart"
              hint={`${stats.sessions7d} sessions · ${stats.pageviews7d} pages vues`}
            />
            <AdminKpiCard
              to="/admin/audience"
              label="Pages vues (7 j)"
              value={stats.pageviews7d}
              icon="globe"
              hint="Mesure d’audience cookies"
            />
            <AdminKpiCard
              to="/admin/audience"
              label="Conversions (7 j)"
              value={stats.conversions7d}
              icon="trophy"
              tone={stats.conversions7d > 0 ? 'ok' : 'default'}
              hint="Inscriptions, contacts…"
            />
            <AdminKpiCard to="/admin/utilisateurs" label="Entrepreneurs" value={stats.users} icon="users" hint="Comptes actifs & bloqués" />
            <AdminKpiCard to="/admin/membres" label="Staff" value={stats.staff} icon="shield" hint="Admins, advisors et jury" />
            <AdminKpiCard to="/admin/exploitation" label="Projets" value={stats.projects} icon="folder" hint={`${stats.viable} viables`} tone="ok" />
            <AdminKpiCard to="/admin/concours" label="Inscriptions concours" value={stats.registrations} icon="trophy" />
            <AdminKpiCard
              to="/admin/assistance"
              label="Préoccupations ouvertes"
              value={stats.openConcerns}
              icon="lifebuoy"
              tone={stats.openConcerns > 0 ? 'danger' : 'default'}
              hint="À traiter"
            />
            <AdminKpiCard
              to="/admin/marketplace"
              label="Demandes marketplace"
              value={stats.inquiries}
              icon="store"
              tone={stats.inquiries > 0 ? 'warn' : 'default'}
            />
            <AdminKpiCard to="/admin/assistance" label="Notifications non lues" value={stats.unreadNotifs} icon="bell" hint="Global plateforme" />
            <AdminKpiCard to="/admin/exploitation" label="Projets viables" value={stats.viable} icon="folder" tone="ok" />
            <AdminKpiCard to="/admin/audience" label="Sessions (7 j)" value={stats.sessions7d} icon="layout" hint="Parcours visiteurs" />
          </div>
        </>
      )}
    </div>
  );
}

export function AdminUsersPage() {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const [users, setUsers] = useState<
    { id: string; email: string; role: string; is_active: boolean; first_name: string; last_name: string }[]
  >([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const { data, error: err } = await kuunda
      .from('profiles')
      .select('id, email, role, is_active, first_name, last_name')
      .eq('role', 'user')
      .order('created_at', { ascending: false });
    if (err) toast.error(err.message);
    else setUsers(data ?? []);
    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        `${u.first_name} ${u.last_name}`.toLowerCase().includes(q),
    );
  }, [users, query]);

  async function toggleActive(id: string, is_active: boolean) {
    if (!isAdmin) return;
    const { error: err } = await kuunda.from('profiles').update({ is_active: !is_active }).eq('id', id);
    if (err) {
      toast.error(err.message);
    } else {
      toast.success(is_active ? 'Compte bloqué.' : 'Compte réactivé.', 'Utilisateurs');
      await load();
    }
  }

  return (
    <div className="admin-page">
      <AdminPageHeader
        title="Utilisateurs"
        description="Entrepreneurs inscrits sur la plateforme."
        actions={
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => void load()}>
            Actualiser
          </button>
        }
      />
      <section className="admin-panel">
        <div className="admin-toolbar">
          <div className="field">
            <label htmlFor="user-search">Recherche</label>
            <input
              id="user-search"
              placeholder="Nom ou email…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
        {loading ? <ListSkeleton rows={5} /> : null}
        {!loading && filtered.length === 0 ? (
          <AdminEmpty icon="users" title="Aucun utilisateur" hint="Ajustez la recherche ou attendez de nouvelles inscriptions." />
        ) : (
          <div className="admin-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Email</th>
                  <th>Statut</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <strong>
                        {u.first_name} {u.last_name}
                      </strong>
                    </td>
                    <td>{u.email}</td>
                    <td>
                      <span className={`admin-status-dot ${u.is_active ? 'on' : 'off'}`} />
                      {u.is_active ? 'Actif' : 'Bloqué'}
                    </td>
                    <td>
                      {isAdmin ? (
                        <div className="admin-row-actions">
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => void toggleActive(u.id, u.is_active)}
                          >
                            {u.is_active ? 'Bloquer' : 'Débloquer'}
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

type MemberRow = {
  id: string;
  email: string;
  role: AppRole;
  is_active: boolean;
  first_name: string;
  last_name: string;
};

const ROLE_LABEL: Record<string, string> = {
  admin: 'Administrateur',
  advisor: 'Advisor',
  jury: 'Jury',
  user: 'Entrepreneur',
};

export function AdminMembersPage() {
  const { isAdmin, profile } = useAuth();
  const toast = useToast();
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [searchEmail, setSearchEmail] = useState('');

  async function load() {
    const { data, error: err } = await kuunda
      .from('profiles')
      .select('id, email, role, is_active, first_name, last_name')
      .in('role', ['admin', 'advisor', 'jury'])
      .order('role');
    if (err) toast.error(err.message);
    else setMembers((data ?? []) as MemberRow[]);
  }

  useEffect(() => {
    void load();
  }, []);

  async function setRole(id: string, role: AppRole) {
    if (!isAdmin) return;
    if (id === profile?.id && role === 'user') {
      toast.error('Vous ne pouvez pas vous rétrograder vous-même.');
      return;
    }
    const { error: err } = await kuunda.from('profiles').update({ role }).eq('id', id);
    if (err) {
      toast.error(err.message);
    } else {
      toast.success('Rôle mis à jour.', 'Membres');
      await load();
    }
  }

  const [promoteRole, setPromoteRole] = useState<AppRole>('advisor');

  async function promoteByEmail() {
    if (!isAdmin || !searchEmail.trim()) return;
    const { data, error: findErr } = await kuunda
      .from('profiles')
      .select('id, email, role')
      .eq('email', searchEmail.trim().toLowerCase())
      .maybeSingle();
    if (findErr) {
      toast.error(findErr.message);
      return;
    }
    if (!data) {
      toast.error('Aucun profil trouvé pour cet email.');
      return;
    }
    const { error: err } = await kuunda.from('profiles').update({ role: promoteRole }).eq('id', data.id);
    if (err) {
      toast.error(err.message);
    } else {
      toast.success(
        `${data.email} affecté au rôle ${ROLE_LABEL[promoteRole].toLowerCase()}.`,
        'Membres',
      );
      setSearchEmail('');
      await load();
    }
  }

  return (
    <div className="admin-page">
      <AdminPageHeader
        title="Membres staff"
        description="Administrateurs, advisors et jury ayant accès au back-office."
      />

      {isAdmin ? (
        <section className="admin-panel">
          <div className="admin-panel-title">
            <h3>Attribuer un rôle staff</h3>
          </div>
          <div className="admin-toolbar">
            <div className="field">
              <label htmlFor="promote-email">Email</label>
              <input
                id="promote-email"
                type="email"
                placeholder="user@exemple.com"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="promote-role">Rôle</label>
              <select id="promote-role" value={promoteRole} onChange={(e) => setPromoteRole(e.target.value as AppRole)}>
                <option value="advisor">{ROLE_LABEL.advisor}</option>
                <option value="jury">{ROLE_LABEL.jury}</option>
                <option value="admin">{ROLE_LABEL.admin}</option>
              </select>
            </div>
            <button type="button" className="btn btn-primary" onClick={() => void promoteByEmail()}>
              Enregistrer le rôle
            </button>
          </div>
        </section>
      ) : (
        <p className="muted">Consultation seule — seuls les admins modifient les rôles.</p>
      )}

      <section className="admin-panel">
        {members.length === 0 ? (
          <AdminEmpty icon="shield" title="Aucun membre staff" />
        ) : (
          <div className="admin-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Email</th>
                  <th>Rôle</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id}>
                    <td>
                      <strong>
                        {m.first_name} {m.last_name}
                      </strong>
                    </td>
                    <td>{m.email}</td>
                    <td>
                      {isAdmin ? (
                        <select value={m.role} onChange={(e) => void setRole(m.id, e.target.value as AppRole)}>
                          <option value="admin">{ROLE_LABEL.admin}</option>
                          <option value="advisor">{ROLE_LABEL.advisor}</option>
                          <option value="jury">{ROLE_LABEL.jury}</option>
                        </select>
                      ) : (
                        ROLE_LABEL[m.role] ?? m.role
                      )}
                    </td>
                    <td>
                      <span className={`admin-status-dot ${m.is_active ? 'on' : 'off'}`} />
                      {m.is_active ? 'Actif' : 'Inactif'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

type ProjectRow = {
  id: string;
  title: string;
  viability: string;
  documents_complete: boolean;
  feasibility_score: number | null;
  created_at: string;
  user_id: string;
  profiles: { email: string; first_name: string; last_name: string } | null;
};

export function AdminExploitationPage() {
  const toast = useToast();
  const [rows, setRows] = useState<ProjectRow[]>([]);
  const [filter, setFilter] = useState<'all' | 'viable' | 'docs'>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void kuunda
      .from('projects')
      .select(
        'id, title, viability, documents_complete, feasibility_score, created_at, user_id, profiles(email, first_name, last_name)',
      )
      .order('created_at', { ascending: false })
      .then(({ data, error: err }) => {
        if (err) toast.error(err.message);
        else setRows((data ?? []) as unknown as ProjectRow[]);
        setLoading(false);
      });
  }, []);

  const visible = rows.filter((r) => {
    if (filter === 'viable') return r.viability === 'viable';
    if (filter === 'docs') return r.documents_complete;
    return true;
  });

  return (
    <div className="admin-page">
      <AdminPageHeader
        title="Exploitation"
        description="Suivi des projets entrepreneurs : viabilité, scores et documents."
      />
      <section className="admin-panel">
        <div className="admin-filters" role="tablist" aria-label="Filtres projets">
          <button type="button" className={`admin-filter${filter === 'all' ? ' is-active' : ''}`} onClick={() => setFilter('all')}>
            Tous ({rows.length})
          </button>
          <button
            type="button"
            className={`admin-filter${filter === 'viable' ? ' is-active' : ''}`}
            onClick={() => setFilter('viable')}
          >
            Viables ({rows.filter((r) => r.viability === 'viable').length})
          </button>
          <button type="button" className={`admin-filter${filter === 'docs' ? ' is-active' : ''}`} onClick={() => setFilter('docs')}>
            Docs complets ({rows.filter((r) => r.documents_complete).length})
          </button>
        </div>
      </section>
      <section className="admin-panel">
        {loading ? <ListSkeleton rows={5} /> : null}
        {!loading && visible.length === 0 ? (
          <AdminEmpty icon="folder" title="Aucun projet" hint="Aucun résultat pour ce filtre." />
        ) : (
          <div className="admin-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Projet</th>
                  <th>Porteur</th>
                  <th>Viabilité</th>
                  <th>Score</th>
                  <th>Docs</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <strong>{r.title}</strong>
                      <div className="muted">{new Date(r.created_at).toLocaleDateString('fr-FR')}</div>
                    </td>
                    <td>
                      {r.profiles ? `${r.profiles.first_name} ${r.profiles.last_name}` : r.user_id.slice(0, 8)}
                      <div className="muted">{r.profiles?.email}</div>
                    </td>
                    <td>
                      <span className={`chip ${r.viability === 'viable' ? 'ok' : 'warn'}`}>
                        {viabilityLabel(r.viability)}
                      </span>
                    </td>
                    <td>{r.feasibility_score ?? '—'}</td>
                    <td>
                      <span className={`chip ${r.documents_complete ? 'ok' : 'warn'}`}>
                        {r.documents_complete ? 'Complets' : 'En cours'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

export function AdminContactPage() {
  const toast = useToast();
  const [rows, setRows] = useState<
    {
      id: string;
      full_name: string;
      email: string;
      subject: string;
      body: string;
      created_at: string;
      is_read?: boolean;
    }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    const { data, error: err } = await kuunda
      .from('contact_messages')
      .select('id, full_name, email, subject, body, created_at, is_read')
      .order('created_at', { ascending: false })
    if (err) toast.error(err.message);
    else setRows(data ?? []);
    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, []);

  const selected = rows.find((row) => row.id === selectedId) ?? null;

  async function openMessage(id: string) {
    setSelectedId(id);
    const row = rows.find((item) => item.id === id);
    if (!row || row.is_read) return;
    setRows((prev) => prev.map((item) => (item.id === id ? { ...item, is_read: true } : item)));
    const { error: err } = await kuunda
      .from('contact_messages')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('id', id);
    if (err) toast.error(err.message);
  }

  return (
    <div className="admin-page">
      <AdminPageHeader title="Contact" description="Messages reçus via le formulaire public." />
      <section className="admin-panel">
        {loading ? <ListSkeleton rows={4} /> : null}
        {!loading && rows.length === 0 ? (
          <AdminEmpty icon="mail" title="Aucun message" hint="Les demandes contact apparaîtront ici." />
        ) : (
          <ul className="admin-list">
            {rows.map((r) => (
              <li
                key={r.id}
                className="admin-list-item"
                style={{
                  cursor: 'pointer',
                  borderLeft: r.is_read ? '4px solid var(--line)' : '4px solid var(--accent)',
                  background: r.is_read ? undefined : 'rgba(74, 126, 176, 0.08)',
                }}
                onClick={() => void openMessage(r.id)}
              >
                <strong style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <span>
                    {r.full_name} — {r.email}
                  </span>
                  <span className={`chip ${r.is_read ? 'ok' : 'warn'}`}>{r.is_read ? 'Vu' : 'Nouveau'}</span>
                </strong>
                <div className="admin-contact-meta muted">
                  <span>{r.subject || 'Sans objet'}</span>
                  <span>{new Date(r.created_at).toLocaleString('fr-FR')}</span>
                </div>
                <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {r.body.length > 180 ? `${r.body.slice(0, 180)}…` : r.body}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      {selected ? (
        <section className="admin-panel">
          <div className="admin-panel-title">
            <h3>Lecture du message</h3>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
            <strong>
              {selected.full_name} — {selected.email}
            </strong>
            <span className="muted">{new Date(selected.created_at).toLocaleString('fr-FR')}</span>
          </div>
          <p className="muted" style={{ marginTop: '0.5rem' }}>
            Sujet : {selected.subject || 'Sans objet'}
          </p>
          <div
            style={{
              whiteSpace: 'pre-wrap',
              maxHeight: '26rem',
              overflowY: 'auto',
              padding: '0.9rem',
              border: '1px solid var(--line)',
              borderRadius: '0.85rem',
              background: 'var(--panel-soft)',
            }}
          >
            {selected.body}
          </div>
        </section>
      ) : null}
    </div>
  );
}

export { AdminConcoursPage } from './AdminConcoursPage';
export { AdminAssistancePage } from './AdminAssistancePage';
export { AdminAgendaPage } from './AdminAgendaPage';
